import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import MarketingPage from '../../../marketing-preferences/MarketingPage';
import RouterPaths from '../../../constants/RouterPaths';
import HomePage from '../../../homepage/HomePage';
import AboutUs from '../../../aboutus/AboutUs';
import Login from '../../../auth/Login';
import ForgotPasswordPage from '../../../auth/forgotpassword/ForgotPasswordPage';
import ConfirmForgotPasswordPage from '../../../auth/forgotpassword/ConfirmForgotPasswordPage';
import ResetPasswordPage from '../../../auth/resetpassword/ResetPasswordPage';
import RegisterPage from '../../../register/RegisterPage';
import ProtectedRoute from '../../hoc/ProtectedRoute';
import SettingsPage from '../../../settings/SettingsPage';
import EditEmployeePage from '../../../settings/employees/edit/EditEmployeePage';
import CreateEmployeePage from '../../../settings/employees/create/CreateEmployeePage';
import AddVehiclePage from '../../../addvehicle/AddVehiclePage';
import EmployeesPage from '../../../settings/employees/EmployeesPage';
import MyProfilePage from '../../../settings/myprofile/MyProfilePage';
import SearchAlertsPage from '../../../settings/searchalerts/SearchAlertsPage';
import NotificationsPage from '../../../settings/notifications/NotificationsPage';
import MyWalletPage from '../../../mywallet/MyWalletPage';
import SearchPage from '../../../search/SearchPage';
import SearchDetailsPage from '../../../search/details/SearchDetailsPage';
import TransactionsPage from '../../../settings/transactions/TransactionsPage';
import InvoicesPage from '../../../settings/invoices/InvoicesPage';
import CustomSite from '../../../customsite/CustomSite';
import MyVehiclesPage from '../../../myvehicles/MyVehiclesPage';
import MyVehiclesDetailsPage from '../../../myvehicles/details/MyVehiclesDetailsPage';
import ConfirmResetPasswordPage from '../../../auth/resetpassword/ConfirmResetPasswordPage';
import MyBidsPage from '../../../mybids/MyBidsPage';
import MyBidsDetailsPage from '../../../mybids/details/MyBidsDetailsPage';
import { getTokenInfo } from '../../../settings/myprofile/MyProfileActions';
import { getPaymentMethod } from '../../../mywallet/MyWalletActions';
import { UserStatus } from '../../../constants/UserStatus';
import ImpersonatePage from '../../../auth/impersonate/ImpersonatePage';
import CreateSearchAlertPage from '../../../settings/searchalerts/create/CreateSearchAlertPage';
import EditSearchAlertsPage from '../../../settings/searchalerts/edit/EditSearchAlertPage';
import Header from '../Header/Header';
import Footer from '../../../footer/Footer';
import AnalyticsPage from '../../../analytics/pages/AnalyticsPage';
import CookiesContainer from '../CookiesContainer';
import DashboardTabs from '../new/DashboardTabs';
import FavouriteOffersPage from '../../../favouriteoffers/FavouriteOffersPage';
import ScrollToTop from '../ScrollToTop';
import FeesInformationPage from '../../../fees/FeesInformationPage';
import NextGearCapitalPage from '../../../next-gear-capital/NextGearCapitalPage';

function DashboardRedirect() {
  const isBuyer = useSelector((state) => state.auth.get('isBuyer'));
  const isSeller = useSelector((state) => state.auth.get('isSeller'));
  if (isSeller) return <Navigate to={RouterPaths.MY_VEHICLES} replace />;
  if (isBuyer) return <Navigate to={RouterPaths.MY_BIDS} replace />;
  return <Navigate to={RouterPaths.SEARCH} replace />;
}

function Content() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [customSiteNotFound, setCustomSiteNotFound] = useState(false);

  const location = useLocation();
  const isSearchPage = location.pathname === RouterPaths.SEARCH;
  const isImpersonatePage = location.pathname === RouterPaths.IMPERSONATE;
  const isAuthenticated = useSelector((state) => state.auth.get('isAuthenticated'));
  const userAuthStatus = useSelector((state) => state.auth.get('status'));
  const isOwner = useSelector((state) => state.auth.get('isOwner'));

  const excludeHeaderPaths = useMemo(
    () => [
      RouterPaths.REGISTER,
      RouterPaths.LOGIN,
      RouterPaths.FORGOT_PASSWORD,
      RouterPaths.CONFIRM_FORGOT_PASSWORD,
      RouterPaths.RESET_PASSWORD,
      RouterPaths.CONFIRM_RESET_PASSWORD,
    ],
    [],
  );

  const isDashboardTabsVisible = useMemo(
    () =>
      [
        RouterPaths.DASHBOARD,
        RouterPaths.MY_VEHICLES,
        RouterPaths.MY_BIDS,
        RouterPaths.FAVOURITE_OFFERS,
        RouterPaths.ANALYTICS,
      ].includes(location.pathname),
    [location.pathname],
  );

  const excludeFooterPaths = useMemo(
    () => [
      RouterPaths.REGISTER,
      RouterPaths.LOGIN,
      RouterPaths.FORGOT_PASSWORD,
      RouterPaths.CONFIRM_FORGOT_PASSWORD,
      RouterPaths.RESET_PASSWORD,
      RouterPaths.CONFIRM_RESET_PASSWORD,
      RouterPaths.ADD_VEHICLE,
      RouterPaths.ADD_VEHICLE_DRAFT,
    ],
    [],
  );

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getTokenInfo());
      if (isOwner && !isImpersonatePage) {
        dispatch(getPaymentMethod());
      }
      if (userAuthStatus === UserStatus.BLOCKED_DUE_TO_PAYMENT) navigate(RouterPaths.SETTINGS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAuthenticated, userAuthStatus, isOwner, navigate]);

  const shouldExcludePath = useCallback(
    (excludePaths) => !excludePaths.some((path) => matchPath(path, location.pathname)),
    [location.pathname],
  );

  const canShowHeader = useCallback(
    () => shouldExcludePath(excludeHeaderPaths),
    [shouldExcludePath, excludeHeaderPaths],
  );
  const canShowFooter = useCallback(
    () => shouldExcludePath(excludeFooterPaths),
    [shouldExcludePath, excludeFooterPaths],
  );
  const shouldShowHeader = useCallback(
    () => canShowHeader() && !customSiteNotFound,
    [canShowHeader, customSiteNotFound],
  );
  const shouldShowFooter = useCallback(
    () => canShowFooter() && !customSiteNotFound,
    [canShowFooter, customSiteNotFound],
  );
  const createProtectedRoute = useCallback(
    (path, Component) => (
      <Route key={path} path={path} element={<ProtectedRoute component={Component} />} />
    ),
    [],
  );

  const publicRoutes = useMemo(
    () => [
      { path: RouterPaths.HOMEPAGE, component: HomePage },
      { path: RouterPaths.LOGIN, component: Login },
      { path: RouterPaths.FORGOT_PASSWORD, component: ForgotPasswordPage },
      { path: RouterPaths.CONFIRM_FORGOT_PASSWORD, component: ConfirmForgotPasswordPage },
      { path: RouterPaths.RESET_PASSWORD, component: ResetPasswordPage },
      { path: RouterPaths.CONFIRM_RESET_PASSWORD, component: ConfirmResetPasswordPage },
      { path: RouterPaths.REGISTER, component: RegisterPage },
      { path: RouterPaths.IMPERSONATE, component: ImpersonatePage },
      { path: RouterPaths.ABOUT_US, component: AboutUs },
      { path: RouterPaths.MARKETING_PREFERENCES, component: MarketingPage },
      { path: RouterPaths.FEES_INFORMATION, component: FeesInformationPage },
      { path: RouterPaths.NEXT_GEAR_CAPITAL, component: NextGearCapitalPage },
    ],
    [],
  );

  const protectedRoutes = useMemo(
    () => [
      { path: RouterPaths.ADD_VEHICLE, component: AddVehiclePage },
      { path: RouterPaths.ADD_VEHICLE_DRAFT, component: AddVehiclePage },
      { path: RouterPaths.SETTINGS, component: SettingsPage },
      { path: RouterPaths.DASHBOARD, component: DashboardRedirect },
      { path: RouterPaths.EMPLOYEES, component: EmployeesPage },
      { path: RouterPaths.EMPLOYEE_DETAILS, component: EditEmployeePage },
      { path: RouterPaths.CREATE_EMPLOYEE, component: CreateEmployeePage },
      { path: RouterPaths.MY_PROFILE, component: MyProfilePage },
      { path: RouterPaths.SEARCH_ALERTS, component: SearchAlertsPage },
      { path: RouterPaths.CREATE_SEARCH_ALERT, component: CreateSearchAlertPage },
      { path: RouterPaths.EDIT_SEARCH_ALERT, component: EditSearchAlertsPage },
      { path: RouterPaths.NOTIFICATIONS, component: NotificationsPage },
      { path: RouterPaths.MY_VEHICLES, component: MyVehiclesPage },
      { path: RouterPaths.ANALYTICS, component: AnalyticsPage },
      { path: RouterPaths.MY_VEHICLES_DETAILS, component: MyVehiclesDetailsPage },
      { path: RouterPaths.MY_WALLET, component: MyWalletPage },
      { path: RouterPaths.SEARCH, component: SearchPage },
      { path: RouterPaths.SEARCH_DETAILS, component: SearchDetailsPage },
      { path: RouterPaths.TRANSACTIONS, component: TransactionsPage },
      { path: RouterPaths.INVOICES, component: InvoicesPage },
      { path: RouterPaths.MY_BIDS, component: MyBidsPage },
      { path: RouterPaths.MY_BIDS_DETAILS, component: MyBidsDetailsPage },
      { path: RouterPaths.FAVOURITE_OFFERS, component: FavouriteOffersPage },
    ],
    [],
  );
  const handleCustomSiteNotFound = useCallback((val) => setCustomSiteNotFound(val), []);

  useEffect(() => {
    if (!isSearchPage) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, isSearchPage]);

  return (
    <>
      {shouldShowHeader() ? <Header /> : ''}
      {isDashboardTabsVisible && <DashboardTabs />}

      <div className="page-content">
        <Routes>
          {publicRoutes.map(({ path, component: Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
          {protectedRoutes.map(({ path, component }) => createProtectedRoute(path, component))}
          <Route path="*" element={<CustomSite onNotFound={handleCustomSiteNotFound} />} />
        </Routes>
      </div>
      {shouldShowFooter() ? <Footer /> : ''}
      <ScrollToTop showAfter={200} />
      <CookiesContainer />
    </>
  );
}

export default Content;
